import { $api, assetApi, $assets, baseAPI } from '@/config/api';
import { isClient } from '@vueuse/shared';
import { useWsStore } from '@/store/ws';
import { defaultLegalFlag } from '@/config/setup';
import { $ex, vipApi } from '@/config/api';
import { getCookie } from '@/utils/cookie';

export const useCommonStore = defineStore('commonStore', {
  state: () => ({
    isMobile: <any>false,
    lang: isClient ? localStorage.getItem('lang') || 'en-US' : 'en-US',
    isDark: isClient ? localStorage.getItem('isDark') || 'dark' : 'dark',
    currencys: <Array<Record<string, any>>>[], // 币列表
    demoCurrencys: <Array<Record<string, any>>>[],
    // currencyMap: <any>{}, // 币map
    symbols: <Array<any>>[], // 交易对列表
    symbolsSource: <Array<any>>[], // 交易对列表
    // symbolMap: {}, // 交易对map
    config: null, // 币，币对，币选项
    addOptionalRef: null,
    openPair: false,
    // 汇率
    rates: <Array<Record<string, any>>>[],
    suffix: '', // 当前的折合汇率
    prefix: '', // 当前折合币种符号
    // 热门交易对
    hotSymbols: <Record<string, any>>{},
    isWithdrawal: false,
    noWithdrawalMsg: '',
    symbolCategories: <Array<Record<string, any>>>[],
    ordersSpotQuicklyForm: {
      price: '',
      show: false,
      x: 0,
      y: 0,
      newPrice: ''
    },
    ordersQuicklyForm: {
      price: '',
      show: false,
      x: 0,
      y: 0,
      newPrice: ''
    },
    // 设备指纹
    fingerprint: getCookie('d'),
    // 交易页网格布局模块关闭提示
    gridItemTips: false,
    loading: {
      currency: false,
      demoCurrency: false,
      spotSymbol: false,
      rates: false
    },
    profitLoss: localStorage.getItem('profit-loss-type') || '1',
    // 合约交易全站开关信息
    futuresStopInfo: {
      stopTrade: false,
      notice: {},
      recoveryTime: ''
    },
    // 币种分类（多层）
    customQuoteTokens: <Array<any>>[],
    frozenReason: null,
    // 最后订阅的汇率
    lastSubRate: '',
    rtcIp: '',
    timeDiff: 0,
    feeList: []
  }),
  getters: {
    $currencys(state) {
      return state.currencys;
    },
    $symbols(state) {
      return state.symbols;
    },
    // 计价货币
    quoteTokens(state) {
      return state.customQuoteTokens || [];
    },
    getSuffix(state): string {
      return state.suffix;
    },
    /**
     * 币种对应的法币汇率
     *
     * 数据格式： {tokenId: rates<legal, value>}
     * @returns object
     */
    ratesMap(state) {
      const { rates } = state;

      const rateMap: Record<string, object> = {};
      rates.forEach((item: Record<string, any>) => {
        rateMap[item.token] = item.rates;
      });

      return rateMap;
    },
    symbolMap(state) {
      const obj: any = {};

      state.symbolsSource?.forEach((item: any) => {
        obj[item.symbolId] = item;
      });

      return obj;
    },
    currencyMap(state) {
      const obj: any = {};

      if (!state.currencys || !state.currencys.length) {
        return obj;
      }

      state.currencys.forEach((item: any) => {
        obj[item.tokenId] = item;
      });

      return obj;
    },
    demoCurrencyMap(state) {
      const obj: any = {};

      if (!state.demoCurrencys || !state.demoCurrencys.length) {
        return obj;
      }

      state.demoCurrencys.forEach((item: any) => {
        obj[item.tokenId] = item;
      });

      return obj;
    },
    // 活动交易对
    activitySymbols(state) {
      return state.symbols.filter((item: any) => item.quickSymbolActivity);
    }
  },
  actions: {
    getCurrentTime() {
      return new Date().getTime() + this.timeDiff;
    },
    async timeCalibration() {
      const before = new Date().getTime();
      const data = await $ex.serverTime({});
      const after = new Date().getTime();
      let originTime = new Date().getTime();
      if (data?.data?.time) {
        originTime = data?.data?.time;
        const requestTime = Math.floor((after - before) / 2);
        this.timeDiff = originTime + requestTime - new Date().getTime();
      }
    },
    setStore(key: string, value: any) {
      this[key as keyof typeof this] = value;
    },
    // 获取所有币种
    async getTokenList() {
      if (this.loading.currency || this.currencys?.length) return;

      this.loading.currency = true;
      const res: any = await baseAPI.getTokenList();
      this.loading.currency = false;

      if (res.success && res.data) {
        this.currencys = res.data?.tokens;
      }
    },
    // 获取币种分类
    async getSymbolCategories() {
      if (this.symbolCategories?.length) return;
      const res: any = await $api.getSymbolCategory();
      if (res.success && res.data) {
        this.symbolCategories = res.data || [];
      }
    },
    // 获取模拟盘币种
    async getDemoTokenList() {
      if (this.loading.demoCurrency || this.demoCurrencys?.length) return;

      this.loading.demoCurrency = true;
      const res: any = await baseAPI.getTokenList({ business_type: 'VIRTUAL' });
      this.loading.demoCurrency = false;

      if (res.success && res.data) {
        this.demoCurrencys = res.data?.tokens?.filter(
          (item: any) => item.virtual
        );
      }
    },
    // 获取现货交易对
    async getSymbols(isUpdate?: boolean) {
      // 拦截请求中或者非更新重复请求
      if (this.loading.spotSymbol || (!isUpdate && this.symbols?.length))
        return;

      this.loading.spotSymbol = true;
      const res: any = await baseAPI.getSpotSymbol({ business_type: 'USDC' });
      this.loading.spotSymbol = false;

      if (res.success) {
        // 兼容下架币对历史数据展示
        this.symbolsSource = res.data?.symbols
          ?.filter((item: any) => {
            return item.showStatus;
          })
          .sort((a: any, b: any) => b.symbolWeight - a.symbolWeight);

        this.symbols = this.symbolsSource?.filter((item: any) => {
          return item.extStatus !== 0;
        });
        // 交易对计价币种及标签分类
        this.customQuoteTokens = res.data?.customQuoteTokens;
      }
    },

    subRates() {
      const wsStore = useWsStore();

      // 当前只订阅一种法币汇率
      if (this.suffix && this.lastSubRate && this.lastSubRate !== this.suffix) {
        wsStore.unSub('rate', wsStore.quoteWS);
      }

      const legalFlag =
        localStorage.getItem('legal_flag') || defaultLegalFlag();

      this.lastSubRate = legalFlag || this.suffix;

      wsStore.quoteWS?.sub(
        {
          id: 'rate',
          topic: 'rate',
          event: 'sub',
          params: {
            tokens: '',
            legalCoins: legalFlag || this.suffix,
            binary: import.meta.env.MODE === 'production'
          }
        },
        () => null,
        (res: any) => {
          if (!res.data) {
            return;
          }

          const { c, r } = res.data;
          // 变化才推送，第一次使用http的全量汇率
          Object.keys(r).forEach((key: string) => {
            const existingIndex = this.rates.findIndex(
              (rate: any) => rate.token === key
            );
            if (existingIndex !== -1) {
              // 如果已存在该tokenId，更新对应元素
              this.rates[existingIndex].rates[this.suffix] = r[key];
            } else {
              this.rates.push({ token: c, rates: { [this.suffix]: r[key] } });
            }
          });
        }
      );
    },
    // 获取汇率
    async getRates(isUpdate?: boolean) {
      if (this.loading.rates || (this.rates?.length && !isUpdate)) {
        return;
      }

      this.loading.rates = true;

      const res: any = await assetApi.getRates();

      if (res.success) {
        this.rates = res.data;
      }

      this.loading.rates = false;
      this.subRates();
    },

    async getHotSymbol() {
      if (this.hotSymbols.length) {
        return;
      }

      const res: any = await $api.getHotSymbol({ number: 10 });

      if (res.success) {
        this.hotSymbols = res.data;
      }
    },
    async getWithdrawal() {
      const res: any = await $assets.quota_info({
        token_id: 'BTC',
        chain_type: 'BTC'
      });
      if (res.code === 200 && res.data) {
        this.isWithdrawal = !res.data?.allowWithdraw;
        this.noWithdrawalMsg = res.data?.refuseReason;
        if (res.data.frozenReason) {
          this.frozenReason = res.data.frozenReason;
        }
      }
    },
    // 币种、现货交易对、汇率等基本数据
    async getBaseData() {
      this.getTokenList();
      this.getSymbols(false);
      this.getRates();
    },
    // 上报设备指纹
    async deviceTokenReport(visitorData: any) {
      $api.deviceTokenReport(visitorData);
    },
    // 获取合约交易全站开关信息
    async getFuturesStopInfo() {
      const res: any = await baseAPI.GetFuturesStopInfo();
      if (res.success) {
        this.futuresStopInfo = {
          stopTrade: res.data.stopTrade,
          notice: res.data.notice || {},
          recoveryTime: res.data.recoveryTime || ''
        };
      }
    },
    // 添加设置费率列表的方法
    async setFeeList(data: never[]) {
      this.feeList = data;
    },

    // 添加获取费率列表的方法
    async fetchFeeList() {
      try {
        const res: any = await vipApi.getWithdrawFeeList({});
        if (res.success && res.data) {
          this.feeList = res.data;
        }
      } catch (error) {
        console.error('获取费率列表失败:', error);
      }
    }
  }
});
